import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { FormikProps, Formik, Form, Field } from "formik";
import { Col, Row } from "react-bootstrap";
import { Sidebar } from "primereact/sidebar";
import { Button } from "primereact/button";
import { get, map, pickBy, isEmpty, sumBy } from "lodash";
import { InputCheckboxField } from "../../../../../components/Shared/InputCheckboxField";
import * as XLSX from "xlsx";
import qs from "qs";
import { request } from "../../../../../utils/Request";
import { utcDateToLocalDate, formatDate } from "../../../../../utils/Helper";

interface IFormProps {
    t?: any | undefined;
    moduleState: any;
    setQuery: any;
    dropdowns: any;
    visibility: any;
    toastify: any;
    onClose?: any;
}

type TInputForm = {
    consider_all_pages: boolean,
    client: boolean,
    status: boolean,
    number: boolean,
    final_total: boolean,
    payment_mode: boolean,
    modified_at: boolean,
    finalize_date: boolean,
    modifier: boolean,
    qty: boolean,
};

const DEFAULT_FORM_STATE = {
    consider_all_pages: true,
    client: true,
    status: true,
    number: true,
    final_total: true,
    payment_mode: true,
    modified_at: true,
    finalize_date: true,
    modifier: true,
    qty: true,
};

// @ts-ignore
function InvoiceExcel(props: IFormProps) {
    const { t, visibility } = props;
    const formRef: any = useRef();
    const [initFormState, setInitFormState] = useState<any>(DEFAULT_FORM_STATE);
    const [panelState, setPanelState] = useState<boolean>(false);

    const onSubmit = async (payloads: TInputForm): Promise<void> => {
        const _params: any = props.moduleState;
        const _payloads: any = payloads;

        if ( payloads.consider_all_pages ) {
            delete _params.page;
            delete _params.per_page;
        }

        try {
            const response = await request({
                url: `/suppliers/archived/invoices`,
                params: _params,
                paramsSerializer: {
                    serialize: (params: any) => qs.stringify(params)
                },
            });

            delete _payloads.consider_all_pages;
            const columns = pickBy(_payloads, (item: boolean) => ( item ));
            const title: any = getTitles();
            const data: any = [];
            map(response.data.data, (value: any) => {
                const invoiceNumber = value.status === 'draft' 
                    ? get(value, ['draft_invoice_number']) 
                    : get(value, ['final_invoice_number']);
                
                const bankTotal = value.payments 
                    ? sumBy(value.payments.filter((i: any) => i.payment_mode === "bank transfer"), 'total') 
                    : 0;
                const cashTotal = value.payments 
                    ? sumBy(value.payments.filter((i: any) => i.payment_mode === "cash"), 'total') 
                    : 0;
                const paymentModeText = `Bank: ${bankTotal}, Cash: ${cashTotal}`;

                const row: any = {
                    client: get(value, ['client', 'name']) || '',
                    status: value.status || '',
                    number: invoiceNumber || '',
                    final_total: value.grand_total || 0,
                    payment_mode: paymentModeText,
                    modified_at: value.updated_at ? utcDateToLocalDate(value.updated_at) : '',
                    finalize_date: value.finalize_date ? formatDate(value.finalize_date, 'DD-MM-YYYY HH:mm') : '',
                    modifier: get(value, ['modifier', 'name']) || '',
                    qty: value.qty || 0,
                };

                const _row: any = {};
                map(columns, (_, key: string) => ( _row[key] = row[key] ));
                const arrayOfRow = Object.keys(_row).map((key) => _row[key]);
                data.push(arrayOfRow);
            });

            const _title = map(columns, (_, key: string) => ( title[key] = title[key] ));
            const excel = XLSX.utils.book_new();
            const sheet = XLSX.utils.aoa_to_sheet([_title, ...data]);
            XLSX.utils.book_append_sheet(excel, sheet, t('invoices'));
            XLSX.writeFile(excel, "archived_invoices.xlsx");
        } catch (e: any) {
            props.toastify(t("server error"), "error");
        }
    };

    const onClose = () => {
        setPanelState(false);

        if ( props.onClose ) {
            props.onClose();
        }
    };

    const getTitles = () => {
        return {
            client: t('client'),
            status: t('status'),
            number: t('invoice number'),
            final_total: t('amount'),
            payment_mode: t('payment mode'),
            modified_at: t('modified at'),
            finalize_date: t('finalize date'),
            modifier: t('modifier'),
            qty: t('qty'),
        };
    };

    useEffect(() => {
        if ( visibility ) {
            setPanelState(visibility);
            setInitFormState({ ...DEFAULT_FORM_STATE, ...props.moduleState.filters ?? [] });
        }
    }, [visibility]);

    return (
        <>
            <div>
                <Sidebar
                    header={
                        <div className="bg-body-tertiary card-header">
                            <h5 className={ "card-title mt-0" }>{ t("export") } { t('invoices') }</h5>
                            <p>
                                { t("select columns to be exported.") }
                            </p>
                        </div>
                    }
                    visible={ panelState } position="left" onHide={ onClose } blockScroll={ true }
                    style={ { width: '400px' } } closeOnEscape={ false }
                >
                    <div style={ {
                        height: '3px',
                        background: 'linear-gradient(90deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 50%)'
                    } }></div>
                    <Formik
                        innerRef={ formRef }
                        enableReinitialize={ true }
                        initialValues={ initFormState }
                        onSubmit={ onSubmit }
                    >
                        { (props: FormikProps<TInputForm>) => {
                            return (
                                <Form onSubmit={ props.handleSubmit }>
                                    <Row className={ "mt-4 md:mb-7 sm:mb-7" }>
                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="consider_all_pages"
                                                component={ InputCheckboxField }
                                                label={ t("consider all pages") }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="client"
                                                component={ InputCheckboxField }
                                                label={ t("client") }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="status"
                                                component={ InputCheckboxField }
                                                label={ t("status") }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="number"
                                                component={ InputCheckboxField }
                                                label={ t("invoice number") }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="final_total"
                                                component={ InputCheckboxField }
                                                label={ t("amount") }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="payment_mode"
                                                component={ InputCheckboxField }
                                                label={ t("payment mode") }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="modified_at"
                                                component={ InputCheckboxField }
                                                label={ t("modified at") }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="finalize_date"
                                                component={ InputCheckboxField }
                                                label={ t("finalize date") }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="modifier"
                                                component={ InputCheckboxField }
                                                label={ t("modifier") }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="qty"
                                                component={ InputCheckboxField }
                                                label={ t("qty") }
                                            />
                                        </Col>
                                    </Row>
                                    <div className="filter filter-card-footer"
                                         style={ { width: '385px' } }>
                                        <Button type={ "submit" } disabled={ props.isSubmitting }
                                                tooltip={ t("export") }
                                                size={ "small" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                        >
                                            <i className="pi pi-file-excel me-2" />
                                            { t("export") }
                                        </Button>
                                        <Button type={ "button" } disabled={ props.isSubmitting }
                                                onClick={ onClose }
                                                tooltip={ t("operation cancel") } className={ "ms-2" }
                                                size={ "small" } severity={ "danger" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                        >
                                            <i className="pi pi-arrow-left me-2" />
                                            { t("close") }
                                        </Button>
                                    </div>
                                </Form>
                            );
                        } }
                    </Formik>
                </Sidebar>
            </div>
        </>
    );
}

InvoiceExcel.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(InvoiceExcel);

